// Deterministic, illustrative demo data. NOT real CIMIS readings.
import { STATIONS } from "./stations";

export const DEMO_DATE = "2026-10-24";
export const CHILL_SEASON_LENGTH = 120; // Nov 1 → end of February
export const CHILL_SEASON_START = "2026-11-01";
export const PRIOR_CHILL_SEASONS = ["2021–22", "2022–23", "2023–24", "2024–25"];
export const LATEST_CHILL_SEASON = "2025–26";
export const UPCOMING_CHILL_SEASON = "2026–27";
export const PRIOR_GDD_SEASONS = ["2022", "2023", "2024", "2025"];
export const CURRENT_GDD_SEASON = "2026";
export const GDD_CURRENT_INDEX = 296; // Oct 24 = day 297 of 2026
export const GDD_SEASON_LENGTH = 365;

const hash = (str) => {
  let h = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

const mulberry32 = (seed) => {
  let a = seed;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const normalizeTo = (arr, target) => {
  const total = arr.reduce((a, b) => a + b, 0);
  return arr.map((v) => (v / total) * target);
};

const chillSeason = (station, season) => {
  const rng = mulberry32(hash(`${station.id}|chill|${season}`));
  const yearFactor = 0.86 + rng() * 0.26;
  const L = CHILL_SEASON_LENGTH;
  const weights = Array.from({ length: L }, (_, d) => {
    let w = Math.pow(Math.sin((Math.PI * (d + 0.5)) / L), 1.3) * (0.55 + rng() * 0.9);
    if (rng() < 0.08) w *= 0.15;
    return w;
  });
  const portionWeights = weights.map((w) => w * (0.82 + rng() * 0.36));
  return {
    dailyHours: normalizeTo(weights, station.climate.chillHours * yearFactor),
    dailyPortions: normalizeTo(portionWeights, station.climate.chillPortions * (1 + (yearFactor - 1) * 0.7)),
  };
};

const degreeDays = (tmin, tmax, base = 50) => {
  if (tmax <= base) return 0;
  if (tmin >= base) return (tmax + tmin) / 2 - base;
  return (tmax - base) ** 2 / (2 * (tmax - tmin));
};

const seasonalTemp = (station, d) =>
  station.climate.meanTemp + station.climate.amplitude * -Math.cos((2 * Math.PI * (d - 18)) / 365);

const gddYear = (station, year) => {
  const rng = mulberry32(hash(`${station.id}|gdd|${year}`));
  const offset = (rng() - 0.5) * 2.4;
  const r = station.climate.diurnal / 2;
  let noise = 0;
  return Array.from({ length: GDD_SEASON_LENGTH }, (_, d) => {
    noise = 0.72 * noise + (rng() - 0.5) * 7;
    const t = seasonalTemp(station, d) + offset + noise;
    return degreeDays(t - r, t + r);
  });
};

const gddClimatology = (station) => {
  const r = station.climate.diurnal / 2;
  return Array.from({ length: GDD_SEASON_LENGTH }, (_, d) => {
    const t = seasonalTemp(station, d);
    return degreeDays(t - r, t + r);
  });
};

const buildStationDataset = (station) => ({
  chill: Object.fromEntries(
    [...PRIOR_CHILL_SEASONS, LATEST_CHILL_SEASON].map((s) => [s, chillSeason(station, s)]),
  ),
  gdd: Object.fromEntries(
    [...PRIOR_GDD_SEASONS, CURRENT_GDD_SEASON].map((y) => [y, gddYear(station, y)]),
  ),
  gddClimatology: gddClimatology(station),
});

export const STATION_DATASETS = Object.fromEntries(
  STATIONS.map((s) => [s.id, buildStationDataset(s)]),
);
